import { createContext, useCallback, useContext, useState, useSyncExternalStore, type ReactNode } from 'react';
import { DEFAULT_RUNDOWN, DEFAULT_TOTALS } from '@brush/ui';
import { createPeriodStore, type PeriodChoices, type PeriodKey, type PeriodStore } from './store';

const PeriodsContext = createContext<PeriodStore | null>(null);

/**
 * The session's period choices (UP25), mounted above the room provider in main.tsx. One store for the life of the tab,
 * created at mount from the shared defaults — All upcoming and This month — and never read from or written to storage.
 */
export function PeriodsProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => createPeriodStore({ 'rundown.period': DEFAULT_RUNDOWN, 'overview.totals': DEFAULT_TOTALS }));
  return <PeriodsContext.Provider value={store}>{children}</PeriodsContext.Provider>;
}

/**
 * A period choice and its setter — the signature part 1b's `usePreference(key)` has (§7.1), so the pickers and the
 * reads do not change when 1b moves the backing from memory to the server.
 */
export function usePeriodChoice<K extends PeriodKey>(key: K): [PeriodChoices[K], (value: PeriodChoices[K]) => void] {
  const store = useContext(PeriodsContext);
  if (!store) throw new Error('usePeriodChoice needs a PeriodsProvider above it (main.tsx).');
  const value = useSyncExternalStore(store.subscribe, () => store.get(key));
  const set = useCallback((next: PeriodChoices[K]) => store.set(key, next), [store, key]);
  return [value, set];
}
